import type { EventBus } from '../EventBus';
import type { MeasurementStore } from '../services/MeasurementStore';
import type { TrackingDebugService } from '../services/TrackingDebugService';
import type { DomEventService, FetchService, LocationService, TimerService } from '../services/browser';
import { CONSENT_POLL_INTERVAL_MS, TRACKING_CUSTOMER_PATH, TRACKING_PAGE_ELEMENT_ID } from '../config/shopware66';
import { TrackingBusEvent } from '../constants/trackingBusEvent';
import { TrackingTag } from '../constants/trackingTag';
import type { PlatformAdapter, ProductData } from './PlatformAdapter';
import { createDefaultConsentStrategies, resolveConsent } from '../consent/resolveConsent';
import { readInjectedTrackingPage } from './shopware66/injectedPage';
import { parseCustomerIdJson } from './shopware66/parseCustomerIdJson';
import {
  attachShopware66MeasurementListeners,
  detachShopware66MeasurementListeners,
} from './shopware66/measurementListeners';
import { isCheckoutFinishUrl } from './shopware66/checkoutFinishPath';

function isInjectedProductData(value: unknown): value is ProductData {
  if (value == null || typeof value !== 'object') {
    return false;
  }
  const v = value as { product_id?: unknown; variant_id?: unknown; price?: unknown };
  if (typeof v.product_id !== 'string') {
    return false;
  }
  if (!(v.variant_id == null || typeof v.variant_id === 'string')) {
    return false;
  }
  if (typeof v.price !== 'number' || !Number.isFinite(v.price)) {
    return false;
  }

  return true;
}

/**
 * Shopware 6.6 storefront {@link PlatformAdapter}: consent resolved through the strategy list in
 * `consent/` (override → Cookiebot → Usercentrics → consentmanager.net →
 * consentmanager.app → Shopware native) and re-probed on a 1s poll,
 * because CMPs load async and expose no stable change event across setups. Also handles Twig JSON
 * injection for PDP context, checkout finish page detection, and customer JSON from
 * `TRACKING_CUSTOMER_PATH`. Theme-specific selectors live in `shopware66/*`.
 */
export class Shopware66Adapter implements PlatformAdapter {
  private readonly strategies = createDefaultConsentStrategies();
  // Only the poll loop in onConsentChange() updates this; hasConsent() resolves fresh every call
  // without touching it. That means hasConsent() can briefly report a value this field hasn't
  // caught up to yet — TrackingClient's initialized/coldStarting guard absorbs that edge on
  // startup. Don't "fix" hasConsent() to sync lastConsent without checking that guard still holds.
  private lastConsent = resolveConsent(this.strategies).granted;
  private consentPollId: number | null = null;

  constructor(
    private readonly fetcher: FetchService,
    private readonly events: DomEventService,
    private readonly timers: TimerService,
    private readonly location: LocationService,
    private readonly debug?: TrackingDebugService,
    private readonly storefrontUrl?: string,
  ) {}

  hasConsent(): boolean {
    return resolveConsent(this.strategies, this.debug).granted;
  }

  onConsentChange(cb: (hasConsent: boolean) => void): void {
    // No CMP exposes a change event we can rely on across all setups, and Cookiebot delivered
    // via GTM can appear seconds after load — so the strategies are re-probed on a poll.
    // Shops can short-circuit the whole list with `window.bvTrackingConsent`.
    if (this.consentPollId != null) {
      return;
    }

    this.consentPollId = this.timers.setInterval(() => {
      const decision = resolveConsent(this.strategies, this.debug);
      if (decision.granted === this.lastConsent) {
        return;
      }
      const prev = this.lastConsent;
      this.lastConsent = decision.granted;
      this.debug?.log('Shopware66Adapter', 'consent changed', {
        from: prev,
        to: decision.granted,
        source: decision.source,
      });
      cb(decision.granted);
    }, CONSENT_POLL_INTERVAL_MS);
  }

  getProductData(): Promise<ProductData> {
    const injected = readInjectedTrackingPage();
    const pageData = injected?.type === 'product-detail' ? injected.pageData : undefined;

    if (isInjectedProductData(pageData)) {
      const data = pageData;
      this.debug?.log('Shopware66Adapter', 'getProductData', { source: 'injected', hasVariant: data.variant_id != null });

      return Promise.resolve(data);
    }

    // Diagnostic flags only (no raw ids / price).
    const el = typeof document !== 'undefined' ? document.getElementById(TRACKING_PAGE_ELEMENT_ID) : null;
    const hasInjectedEl = el != null;
    const txt = (el?.textContent ?? '').trim();
    const hasText = txt.length > 0;
    let parseOk = false;
    if (hasText) {
      try {
        JSON.parse(txt);
        parseOk = true;
      } catch {
        parseOk = false;
      }
    }

    this.debug?.log('Shopware66Adapter', 'getProductData failed (missing injected JSON)', {
      hasInjectedEl,
      hasText,
      parseOk,
      injectedType: injected?.type ?? null,
      hasPageData: injected?.pageData != null,
    });

    return Promise.reject(new Error('Shopware66Adapter: missing or malformed injected product page JSON'));
  }

  async getCustomerId(): Promise<string | null> {
    // The route lives under the sales channel base path (e.g. /en), so an origin-absolute
    // path 400s on domains configured with a URL prefix — prefix with the storefront URL.
    const url = `${this.storefrontUrl?.replace(/\/+$/, '') ?? ''}${TRACKING_CUSTOMER_PATH}`;

    const res = await this.fetcher.fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      credentials: 'same-origin',
    });

    this.debug?.log('Shopware66Adapter', 'getCustomerId', { ok: res.ok, status: res.status });
    if (!res.ok) {
      return null;
    }

    const json = (await res.json()) as unknown;

    return parseCustomerIdJson(json);
  }

  attachMeasurementListeners(store: MeasurementStore): void {
    attachShopware66MeasurementListeners(this.events, store);
  }

  detachMeasurementListeners(): void {
    detachShopware66MeasurementListeners();
  }

  attachPurchaseDetector(bus: EventBus): void {
    const url = this.location.getUrl();
    if (isCheckoutFinishUrl(url)) {
      bus.emit(TrackingBusEvent.PurchaseCompleted, undefined, { publisher: TrackingTag.Shopware66Adapter });
    }
  }

  onProductPageEnter(_callback: () => void): void {
    // Default storefront is full page reload; SPA themes should extend the adapter and invoke the callback.
  }
}
